/**
 * Simple Admin Endpoint Manager
 * Manages backend endpoints for admin authentication and management
 */

const STORAGE_KEY = 'admin-backend-endpoint';

// Default endpoint
const DEFAULT_ENDPOINT = 'https://beta-auth.wdtp.io';

// Get stored endpoint from localStorage
export const getStoredEndpoint = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return parsed.url || DEFAULT_ENDPOINT;
    } catch (e) {
      return stored || DEFAULT_ENDPOINT;
    }
  }
  return DEFAULT_ENDPOINT;
};

// Store endpoint in localStorage
export const storeEndpoint = (url) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ url, updatedAt: new Date().toISOString() }));
  console.log('[AdminEndpoint] Endpoint stored:', url);
};

// Get current endpoint
export const getAdminEndpoint = () => {
  return getStoredEndpoint();
};

// Set admin endpoint
export const setAdminEndpoint = (url) => {
  if (!url) {
    console.warn('[AdminEndpoint] Empty URL provided');
    return;
  }
  storeEndpoint(url);
};

// Get full URL for an auth endpoint
export const getAuthUrl = (endpoint) => {
  const baseUrl = getAdminEndpoint();
  return `${baseUrl}${endpoint}`;
};

// Auth endpoints configuration
export const AUTH_ENDPOINTS = {
  SETUP_STATUS: '/api/admin/auth/setup-status',
  SETUP: '/api/admin/auth/setup',
  LOGIN: '/api/admin/auth/login',
  LOGOUT: '/api/admin/auth/logout',
  ME: '/api/admin/auth/me',
  CHANGE_PASSWORD: '/api/admin/auth/change-password',
  SESSIONS: '/api/admin/auth/sessions',
  LOGOUT_ALL: '/api/admin/auth/logout-all'
};

// Management endpoints configuration
export const MANAGEMENT_ENDPOINTS = {
  ADMINS: '/api/admin/admins',
  ADMIN_BY_ID: (id) => `/api/admin/admins/${id}`,
  CREATE_ADMIN: '/api/admin/admins',
  UPDATE_ADMIN: (id) => `/api/admin/admins/${id}`,
  CHANGE_ROLE: (id) => `/api/admin/admins/${id}/role`,
  RESET_PASSWORD: (id) => `/api/admin/admins/${id}/reset-password`,
  DELETE_ADMIN: (id) => `/api/admin/admins/${id}`
};

// Get all available endpoints for UI
export const getAllAdminEndpoints = () => {
  const baseUrl = getAdminEndpoint();
  return {
    baseUrl,
    auth: {
      name: 'Admin Auth',
      endpoints: Object.entries(AUTH_ENDPOINTS).map(([key, path]) => ({
        name: key,
        path,
        fullUrl: `${baseUrl}${path}`
      }))
    },
    management: {
      name: 'Admin Management',
      endpoints: Object.entries(MANAGEMENT_ENDPOINTS)
        .filter(([key]) => !key.includes('(')) // Filter out dynamic functions
        .map(([key, path]) => ({
          name: key,
          path,
          fullUrl: `${baseUrl}${path}`
        }))
    }
  };
};

// List of common endpoints for quick access
export const PRESET_ENDPOINTS = [
  { name: 'WEB2', url: 'https://beta-auth.wdtp.io' },
  { name: 'WEB3', url: 'https://beta-3.0-auth.wdtp.io' }
];

// Reset to default endpoint
export const resetToDefaultEndpoint = () => {
  storeEndpoint(DEFAULT_ENDPOINT);
  console.log('[AdminEndpoint] Reset to default:', DEFAULT_ENDPOINT);
  return DEFAULT_ENDPOINT;
};

// Initialize endpoint on module load
const init = () => {
  if (!localStorage.getItem(STORAGE_KEY)) {
    storeEndpoint(DEFAULT_ENDPOINT);
    console.log('[AdminEndpoint] Initialized with default:', DEFAULT_ENDPOINT);
  }
};

init();
